import {
  AlertCondition,
  AlertPriority,
  AlertCategory,
  PredefinedCategory,
} from "../types/alerts";

export const ALERT_CONDITION_OPTIONS: AlertCondition[] = [
  "the query has any results",
  "the query returns more than X rows",
  "the query returns X or more rows",
  "the query returns exactly X rows",
  "the query returns more than X% of baseline",
  "the error rate exceeds X%",
  "the count exceeds X standard deviations from baseline",
];

export const ALERT_PRIORITY_OPTIONS: AlertPriority[] = ["P0", "P1", "P2", "P3"];

export const PREDEFINED_CATEGORIES: PredefinedCategory[] = [
  "DATA_BREACH",
  "DATA_EXFILTRATION",
  "POLICY_VIOLATION",
  "PRIVACY_VIOLATION",
  "SECURITY_INCIDENT",
  "PERFORMANCE",
];

export const ALERT_CATEGORY_OPTIONS: AlertCategory[] = [...PREDEFINED_CATEGORIES, "CUSTOM"];

export const isPredefinedCategory = (category: AlertCategory): category is PredefinedCategory => {
  return PREDEFINED_CATEGORIES.includes(category as PredefinedCategory);
};

export const formatCategoryLabel = (category: AlertCategory, customName?: string): string => {
  if (category === "CUSTOM" && customName) {
    return customName;
  }
  if (category === "CUSTOM") {
    return "Custom";
  }
  if (isPredefinedCategory(category)) {
    return category
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  // Custom category name
  return category;
};

export const ALERT_TEMPLATES: Record<
  PredefinedCategory | "CUSTOM",
  { query: string; priority: AlertPriority; description: string }
> = {
  DATA_BREACH: {
    query: "Alert me when there are data breach incidents detected from any security scanner",
    priority: "P0",
    description: "Critical data breach detection alerts",
  },
  DATA_EXFILTRATION: {
    query: "Notify me when data exfiltration attempts are detected by the relevance scanner",
    priority: "P0",
    description: "Monitor for data exfiltration patterns",
  },
  POLICY_VIOLATION: {
    query: "Alert when any policy violations are detected in AI agent responses",
    priority: "P1",
    description: "Track policy compliance violations",
  },
  PRIVACY_VIOLATION: {
    query: "Alert me when PII or sensitive information is detected in model outputs",
    priority: "P1",
    description: "Privacy and PII protection alerts",
  },
  SECURITY_INCIDENT: {
    query: "Notify me when security incidents are detected with high or critical severity",
    priority: "P1",
    description: "General security incident monitoring",
  },
  PERFORMANCE: {
    query: "Alert when model performance degrades or latency exceeds thresholds",
    priority: "P2",
    description: "Performance monitoring and optimization alerts",
  },
  CUSTOM: {
    query: "",
    priority: "P2",
    description: "Custom alert configuration",
  },
};

export const PRIORITY_DESCRIPTIONS: Record<
  AlertPriority,
  { label: string; sla: string; color: string }
> = {
  P0: {
    label: "Critical - Immediate Response",
    sla: "15 minutes",
    color: "red",
  },
  P1: {
    label: "High - Urgent",
    sla: "1 hour",
    color: "orange",
  },
  P2: {
    label: "Medium - Important",
    sla: "4 hours",
    color: "yellow",
  },
  P3: {
    label: "Low - Standard",
    sla: "1 business day",
    color: "gray",
  },
};
